import { useEffect } from "react";
import { X } from "lucide-react";
import { PanneauContainer } from "./PanneauContainer";
import type { Panneau as PanneauModel } from "../api/types";

interface Props {
  conv: string;
  summary: PanneauModel;
  onClose: () => void;
}

/**
 * Full-viewport modal showing a single panneau enlarged.
 * Closes via Escape, the close button, or click on the backdrop.
 */
export function ExpandedPanneauModal({ conv, summary, onClose }: Props) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 dark:bg-white/40 flex items-start justify-center overflow-y-auto p-4 sm:p-8"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Panneau ${summary.name} déployé`}
    >
      <div
        className="w-full max-w-5xl bg-canvas dark:bg-canvas-dark rounded-2xl shadow-2xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button: tucked outside the frame on sm+ (Krug-pref);
            on phones with notches it could clip, so on mobile it sits
            inside the top-right of the modal. */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          className="absolute top-2 right-2 sm:-top-3 sm:-right-3 h-9 w-9 rounded-full bg-white dark:bg-surface-dark shadow-md flex items-center justify-center hover:bg-canvas dark:hover:bg-canvas-dark border border-black/10 dark:border-white/10 z-10"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="p-2 sm:p-4">
          <PanneauContainer conv={conv} summary={summary} />
        </div>
      </div>
    </div>
  );
}
